import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';

import { fetchUsers, updateUser, deleteUser, resetUserPassword } from '../../store/slices/userSlice';
import { RootState, AppDispatch } from '../../store/store';
import useModernAlert from '../../hooks/useModernAlert';

const roleColors = {
  admin: 'error',
  user: 'primary',
  superadmin: 'warning'
} as const;

const roleLabels = {
  admin: 'Administrátor',
  user: 'Uživatel',
  superadmin: 'Super Administrátor'
} as const;

const Users: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { showError, showSuccess, showConfirm, AlertComponent } = useModernAlert();
  const { users, loading, error } = useSelector((state: RootState) => state.users);
  const { user: currentUser } = useSelector((state: RootState) => state.auth);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [editingUser, setEditingUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user'
  });
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState<any>(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);



  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      const updateData = { ...formData } as any;
      
      await dispatch(updateUser({ id: editingUser.id, data: updateData })).unwrap();
      setEditingUser(null);
      resetForm();
      dispatch(fetchUsers());
    } catch (error) {
      console.error('Update user error:', error);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (userId === currentUser?.id) {
      showError('Nemůžete smazat svůj vlastní účet');
      return;
    }

    showConfirm({
      title: '🗑️ Smazat uživatele',
      message: 'Opravdu chcete smazat tohoto uživatele? Tato akce nelze vrátit zpět.',
      type: 'warning',
      confirmText: 'Smazat',
      onConfirm: async () => {
        try {
          await dispatch(deleteUser(userId)).unwrap();
          dispatch(fetchUsers());
          showSuccess('Uživatel byl úspěšně smazán');
        } catch (error) {
          console.error('Delete user error:', error);
          showError('Chyba při mazání uživatele');
        }
      }
    });
  };

  const handleResetPassword = async () => {
    if (!selectedUserForReset || !newPassword) return;

    try {
      await dispatch(resetUserPassword({ id: selectedUserForReset.id, newPassword })).unwrap();
      setResetPasswordDialog(false);
      setSelectedUserForReset(null);
      setNewPassword('');
    } catch (error) {
      console.error('Reset password error:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: 'user'
    });
  };

  const openEditDialog = (user: any) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role
    });
  };

  const filteredUsers = users.filter((user: any) => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Jméno',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          <Typography variant="body2" fontWeight="bold">
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={roleLabels[params.value as keyof typeof roleLabels] || params.value}
          color={roleColors[params.value as keyof typeof roleColors] || 'default'}
          size="small"
          icon={params.value === 'admin' ? <AdminIcon /> : <PersonIcon />}
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Vytvořeno',
      width: 150,
      valueGetter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return isNaN(date.getTime()) ? '' : format(date, 'dd.MM.yyyy', { locale: cs });
      }
    },
    {
      field: 'last_login',
      headerName: 'Poslední přihlášení',
      width: 150,
      valueGetter: (params) => {
        if (!params.value) return 'Nikdy';
        const date = new Date(params.value);
        return isNaN(date.getTime()) ? 'Nikdy' : format(date, 'dd.MM.yyyy HH:mm', { locale: cs });
      }
    },
    {
      field: 'actions',
      headerName: 'Akce',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => openEditDialog(params.row)}
            title="Upravit"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedUserForReset(params.row);
              setResetPasswordDialog(true);
            }}
            title="Resetovat heslo"
          >
            <LockResetIcon />
          </IconButton>
          {params.row.id !== currentUser?.id && (
            <IconButton
              size="small"
              onClick={() => handleDeleteUser(params.row.id)}
              title="Smazat"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Uživatelé
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Uživatelé se registrují sami přes registrační formulář
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Hledat"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                label="Role"
              >
                <MenuItem value="all">Všechny role</MenuItem>
                {Object.entries(roleLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setRoleFilter('all');
              }}
            >
              Vymazat filtry
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Celkem uživatelů
              </Typography>
              <Typography variant="h4">
                {users.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Administrátoři
              </Typography>
              <Typography variant="h4" color="error.main">
                {users.filter(u => u.role === 'admin').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Uživatelé
              </Typography>
              <Typography variant="h4" color="primary.main">
                {users.filter(u => u.role === 'user').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Aktivní uživatelé
              </Typography>
              <Typography variant="h4" color="success.main">
                {users.filter(u => u.lastLogin).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredUsers}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
            }}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
          />
        )}
      </Paper>



      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog} onClose={() => setResetPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Resetovat heslo pro {selectedUserForReset?.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nové heslo"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialog(false)}>
            Zrušit
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            disabled={!newPassword}
          >
            Resetovat heslo
          </Button>
        </DialogActions>
      </Dialog>
      {AlertComponent}
    </Box>
  );
};

export default Users; 